import { RefreshCw, KeyRound } from 'lucide-react';
import { Botao } from './ui/Botao.js';
import { Pill } from './ui/Pill.js';
import { ListaDs } from './ListaDs.js';

/**
 * Rollover de chave DNSSEC — a parte da tela compartilhada por domínio e bloco.
 *
 * O rollover é semi-automático porque o DS vive no pai (registro.br ou o RIR),
 * fora do nosso alcance: trocamos a chave e assinamos com as duas, mas quem
 * publica o DS novo e remove o velho é uma pessoa. Por isso a tela guia os dois
 * passos manuais e deixa claro que a chave velha só sai quando o pai tiver só o
 * DS novo — o sistema detecta isso sozinho e conclui.
 */

export interface EstadoRollover {
  keyTagVelho: number | null;
  keyTagNovo: number | null;
  dsNovo: string[];
}

export function SecaoRollover({
  assinada,
  rollover,
  aoIniciar,
  iniciando,
  aoVerificar,
  verificando,
  aoCancelar,
  cancelando,
  detectadoNovo,
}: {
  assinada: boolean;
  rollover: EstadoRollover | null;
  aoIniciar: () => void;
  iniciando: boolean;
  aoVerificar: () => void;
  verificando: boolean;
  aoCancelar: () => void;
  cancelando: boolean;
  /** Resultado da última verificação: DS novo já visto no pai? */
  detectadoNovo?: boolean;
}) {
  // Sem assinatura não há o que rotacionar; com rollover em curso mostramos o
  // andamento em vez do botão.
  if (!assinada) return null;

  if (!rollover) {
    return (
      <div className="mt-3 border-t border-borda-suave pt-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[12.5px] text-texto-suave">
            <KeyRound size={14} />
            <span>Rotacionar a chave (CSK) desta zona</span>
          </div>
          <Botao tamanho="tabela" onClick={aoIniciar} carregando={iniciando} iconeEsquerda={<RefreshCw size={13} />}>
            Rotacionar chave
          </Botao>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2.5 rounded-card border border-aviso/40 bg-aviso-fundo p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <RefreshCw size={15} className="text-aviso" />
          <span className="text-[13px] font-bold">Rollover em andamento</span>
        </div>
        <Pill tom="aviso">
          {rollover.keyTagVelho ?? '—'} → {rollover.keyTagNovo ?? '—'}
        </Pill>
      </div>

      <ol className="ml-4 list-decimal space-y-1 text-[12px] leading-relaxed text-texto-medio">
        <li>
          Publique o <strong>DS novo</strong> (keytag {rollover.keyTagNovo ?? '—'}) no pai da zona, ao
          lado do atual.
        </li>
        <li>
          Quando o novo estiver no ar, <strong>remova o DS velho</strong> (keytag{' '}
          {rollover.keyTagVelho ?? '—'}).
        </li>
        <li>
          O sistema detecta quando só o DS novo está no pai e <strong>aposenta a chave velha
          sozinho</strong>. Nunca antes — retirar com o DS velho ainda lá quebraria a validação.
        </li>
      </ol>

      <ListaDs ds={rollover.dsNovo} />

      {detectadoNovo && (
        <p className="text-[11.5px] font-semibold text-info">
          DS novo já visto no pai. Falta o DS velho sair para a chave velha ser aposentada.
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <button
          onClick={aoCancelar}
          disabled={cancelando}
          className="text-[12px] font-semibold text-texto-suave hover:text-erro"
        >
          cancelar rollover
        </button>
        <Botao tamanho="tabela" onClick={aoVerificar} carregando={verificando} iconeEsquerda={<RefreshCw size={13} />}>
          Verificar agora
        </Botao>
      </div>
    </div>
  );
}
